import { useMemo, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { me } from '../api/portal';
import { AccountPanel } from '../components/AccountPanel';
import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { OrigoMark } from '../components/LoginShell';
import { signOutFromPortal } from '../lib/impersonation';
import { useT } from '../lib/i18n';
import {
  hasClips,
  hasHall,
  hasLine,
  hasOffice,
  hasOnAir,
  hasScouting,
  hasSocial,
  hasWire,
  portalKind,
} from '../lib/portalRole';
import {
  IconChart,
  IconFilm,
  IconGlobe,
  IconGrid,
  IconKey,
  IconLink,
  IconLogout,
  IconPlay,
  IconSettings,
  IconShare,
  IconSidebar,
  IconStudio,
  IconUsers,
  IconWhistle,
  IconWire,
} from '../components/icons';
import { PortalErrorBoundary } from '../components/PortalErrorBoundary';
import { useClientIdleSession } from '../lib/idleSession';
import { useAuthStore } from '../stores/auth';

const NAV_KEY = 'dp-client-nav-v2';

type IconCmp = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
type NavItem = { to: string; label: string; icon: IconCmp; end?: boolean };

function readCollapsed(): boolean {
  try {
    const raw = localStorage.getItem(NAV_KEY);
    if (raw === null) return false;
    return raw === '1';
  } catch {
    return true;
  }
}

function writeCollapsed(next: boolean) {
  try { localStorage.setItem(NAV_KEY, next ? '1' : '0'); } catch { /* ignore */ }
}

function initials(name?: string, email?: string): string {
  const src = (name || email || 'CL').trim();
  if (src.includes('@')) {
    const local = src.split('@')[0] ?? src;
    const parts = local.split(/[._-]/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function Layout({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  useClientIdleSession();
  const [accountOpen, setAccountOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const desk = location.pathname.startsWith('/clips') || location.pathname.startsWith('/videos');
  const { t } = useT();
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me, enabled: !!user });
  const nav = useMemo(() => {
    const kind = portalKind(profile.data);
    const who = profile.data;
    const match: NavItem[] = [];
    const broadcast: NavItem[] = [];
    const media: NavItem[] = [];
    const org: NavItem[] = [];
    const account: NavItem[] = [{ to: '/setup', label: t('nav.setup'), icon: IconSettings }];

    if (kind === 'club') match.push({ to: '/home', label: t('nav.home'), icon: IconGrid });
    match.push({ to: '/matches', label: t('nav.matches'), icon: IconWhistle });
    if (hasWire(who)) broadcast.push({ to: '/wire', label: t('nav.wire'), icon: IconWire });
    if (kind !== 'press' && hasOnAir(who)) {
      broadcast.push({ to: '/onair', label: t('nav.onair'), icon: IconPlay });
    }
    if (kind !== 'press' && hasLine(who)) {
      broadcast.push({ to: '/line', label: t('nav.line'), icon: IconChart });
    }
    if (kind !== 'press') {
      if (kind === 'league' && hasOffice(who)) match.push({ to: '/rounds', label: t('nav.rounds'), icon: IconGrid });
      if (hasClips(who)) {
        media.push({ to: '/clips', label: t('nav.clips'), icon: IconFilm, end: true });
        media.push({ to: '/videos', label: t('nav.videos'), icon: IconPlay });
        media.push({ to: '/studio', label: t('nav.studio'), icon: IconStudio });
      }
      match.push({ to: '/history', label: t('nav.history'), icon: IconChart });
      if (hasSocial(who)) media.push({ to: '/social', label: t('nav.social'), icon: IconShare });
      if (kind === 'league') {
        if (hasHall(who)) org.push({ to: '/sponsor', label: t('nav.sponsor'), icon: IconChart });
        org.push({ to: '/cards', label: t('nav.cards'), icon: IconUsers });
        if (hasOffice(who)) org.push({ to: '/press', label: t('nav.press'), icon: IconUsers });
        if (hasOffice(who)) org.push({ to: '/discipline', label: t('nav.discipline'), icon: IconWhistle });
        if (hasHall(who)) org.push({ to: '/venues', label: t('nav.venues'), icon: IconGrid });
        org.push({ to: '/clubs', label: t('nav.clubs'), icon: IconUsers });
      }
      if (hasScouting(who)) org.push({ to: '/scouting', label: t('nav.scouting'), icon: IconUsers });
      account.push(
        { to: '/keys', label: t('nav.keys'), icon: IconKey },
        { to: '/sites', label: t('nav.sites'), icon: IconGlobe },
        { to: '/usage', label: t('nav.usage'), icon: IconChart },
        { to: '/webhooks', label: t('nav.webhooks'), icon: IconLink },
      );
    }
    return [
      { label: t('nav.group.match'), items: match },
      { label: t('nav.group.broadcast'), items: broadcast },
      { label: t('nav.group.media'), items: media },
      { label: t('nav.group.org'), items: org },
      { label: t('nav.account'), items: account },
    ].filter((group) => group.items.length > 0);
  }, [profile.data, t]);

  const toggleNav = () => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(next);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col bg-base text-slate-100">
      <ImpersonationBanner onExit={() => { logout(); }} />
      <div className="flex min-h-0 flex-1">
      <aside className={clsx(
        'sidebar-rail flex shrink-0 flex-col border-r border-white/[0.06] bg-surface',
        collapsed && 'is-collapsed',
      )}>
        <div className="sidebar-head flex h-14 shrink-0 items-center gap-2.5 border-b border-white/[0.06] px-4">
          <OrigoMark className="h-8 w-8 shrink-0" />
          <div className="sidebar-copy min-w-0 flex-1 leading-none">
            <div className="truncate text-[16px] font-semibold tracking-[-0.03em] text-white">origo</div>
            <div className="mt-1 truncate text-[9.5px] font-medium uppercase tracking-[0.18em] text-subtle">{t('nav.client')}</div>
          </div>
          <button
            type="button"
            onClick={toggleNav}
            title={t('nav.collapse')}
            aria-hidden={collapsed}
            tabIndex={collapsed ? -1 : 0}
            className="sidebar-open-only rounded-md p-1.5 text-subtle hover:bg-white/5 hover:text-white"
          >
            <IconSidebar size={16} />
          </button>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-5 overflow-x-hidden overflow-y-auto px-2.5 py-4 text-[13px]">
          {nav.map((group) => (
            <div key={group.label}>
              <div className="sidebar-group px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-subtle/60">
                {group.label}
              </div>
              <div className="flex flex-col gap-0.5">
                {group.items.map((n) => {
                  const Icon = n.icon;
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      title={n.label}
                      className={({ isActive }) =>
                        clsx(
                          'sidebar-item group relative flex min-h-9 items-center rounded-lg transition-colors',
                          isActive
                            ? 'bg-accent/[0.14] font-medium text-white ring-1 ring-inset ring-accent/30'
                            : 'text-subtle hover:bg-white/[0.05] hover:text-white',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span className="absolute -left-2.5 bottom-1.5 top-1.5 w-[3px] rounded-r-full bg-accent" />
                          )}
                          <Icon size={16} className={clsx('shrink-0 transition-colors', isActive ? 'text-accent' : 'text-subtle group-hover:text-slate-200')} />
                          <span className="sidebar-copy truncate">{n.label}</span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-white/[0.06] p-2.5">
          <button
            type="button"
            onClick={toggleNav}
            title={t('nav.expand')}
            aria-hidden={!collapsed}
            tabIndex={collapsed ? 0 : -1}
            className="sidebar-closed-only flex w-full items-center justify-center rounded-md p-1.5 text-subtle hover:bg-white/5 hover:text-white"
          >
            <IconSidebar size={16} />
          </button>
          <div className="sidebar-foot flex items-center gap-2">
            {user && (
              <button
                type="button"
                onClick={() => setAccountOpen(true)}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1 py-0.5 text-left hover:bg-white/5"
                title={t('nav.accountBtn')}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-accent to-[#3b5bdb] text-[10.5px] font-bold text-white ring-2 ring-surface">
                  {initials(user.company_name, user.contact_email)}
                </span>
                <span className="sidebar-copy min-w-0">
                  <span className="block truncate text-[12px] font-medium text-white">{user.company_name}</span>
                  <span className="block truncate text-[10px] text-subtle">{user.contact_email}</span>
                </span>
              </button>
            )}
            <button
              type="button"
              className="flex items-center rounded-md p-1.5 text-[12px] text-subtle hover:bg-white/5 hover:text-white"
              title={t('nav.signout')}
              onClick={() => {
                signOutFromPortal(logout, () => navigate('/login'));
              }}
            >
              <IconLogout size={14} />
              <span className="sidebar-copy pl-1.5">{t('nav.signout')}</span>
            </button>
          </div>
        </div>
      </aside>

      <AccountPanel open={accountOpen} onClose={() => setAccountOpen(false)} />
      <main className={desk ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'min-h-0 flex-1 overflow-y-auto'}>
        <div className={desk ? 'flex min-h-0 flex-1 flex-col' : 'mx-auto w-full max-w-[1480px] px-5 py-6 sm:px-7'}>
          <PortalErrorBoundary>{children}</PortalErrorBoundary>
        </div>
      </main>
      </div>
    </div>
  );
}
